import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export interface Installment {
  amount: number;
  date: string;
}

export interface Payment {
  _id?: string;
  userId: string | any;
  planId: string | any;
  amount: number;
  date: string;
  method: string;
  status: string;
  installments?: Installment[];
  gymId?: string;
  deleted?: boolean;
  isOffer?: boolean;
  endDate?: string;
}

export interface PaymentRequest {
  userId: string;
  planId: string;
  amount: number;
  date: string;
  method: string;
  status: string;
  installments?: Array<{
    amount: number;
    date: string;
  }>;
  gymId: string;
}

export interface PaymentUpdateRequest {
  id: string;
  payment: Partial<PaymentRequest>;
}

export interface PaymentDeleteRequest {
  id: string;
  gymId: string;
}

// Get payments by date range
export const usePayments = (
  startDate: string,
  endDate: string,
  gymId: string
) => {
  return useQuery({
    queryKey: ['payments', startDate, endDate, gymId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/payments/${startDate}/${endDate}?gymId=${gymId}`
      );
      return response.data;
    },
    enabled: !!startDate && !!endDate,
  });
};

export const useAddPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payment: PaymentRequest) => {
      const response = await apiClient.post('/payments', payment);
      return response.data;
    },
    onSuccess: () => {
      // We don't know which date range is currently being viewed, so invalidate all payment queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
};

export const useUpdatePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payment }: PaymentUpdateRequest) => {
      const response = await apiClient.patch(`/payments/${id}`, payment);
      return response.data;
    },
    onSuccess: () => {
      // We don't know which date range is currently being viewed, so invalidate all payment queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
};

export const useDeletePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, gymId }: PaymentDeleteRequest) => {
      const response = await apiClient.delete(`/payments/${id}?gymId=${gymId}`);
      return response.data;
    },
    onSuccess: () => {
      // We don't know which date range is currently being viewed, so invalidate all payment queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
};

export interface UpgradePaymentRequest {
  paymentId: string;
  newPlanId: string;
}

export const useUpgradePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpgradePaymentRequest) => {
      const response = await apiClient.post('/payments/upgrade', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
